import { useCallback, useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  CircularProgress,
  InputBase,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { motion } from "framer-motion";

import muzukuruLogo from "../../../assets/muzukurublack.png";
import ctaImage1 from "../../../assets/cta-pattern.png";
import ctaImage2 from "../../../assets/backgroundImage.jpg";
import { RootState } from "../../../store/Index";
import apiService, { BaseURL } from "../../../service/Api";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../components/common/ShowToast";
import { IThemeState, setTheme } from "../../../store/slice/ThemeSlice";
import CtaSlideshow from "./CTASlideshow";

const Setup2FA = () => {
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [secretKey, setSecretKey] = useState("");
  const [otp, setOtp] = useState<string[]>(new Array(6).fill(""));
  const [isLoading, setIsLoading] = useState(false);
  const [qrError, setQrError] = useState(false);

  // Refs for managing focus between the 6 inputs
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const reduxTheme = useSelector((state: RootState) => state.theme);
  const twoFaUsername = useSelector(
    (state: RootState) => state.auth.twoFaUsername,
  );

  const muzukuruLogoToUse = reduxTheme.logo || muzukuruLogo;
  const ctaImages = [ctaImage1, ctaImage2];

  const isStaging =
    BaseURL?.includes("staging.muzukuru.com") || BaseURL?.includes("staging");

  const fetch2FASecret = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await apiService.get<{
        qr_code_url: string;
        secret_key: string;
      }>("/api/authentication/2fa/setup/");
      setQrCodeUrl(response.data.qr_code_url);
      setSecretKey(response.data.secret_key);
    } catch (err: unknown) {
      console.error("2FA setup error:", err);
      const errorResponse = (
        err as {
          response?: { status?: number; data?: { error?: string } };
        }
      )?.response;

      if (
        errorResponse?.status === 400 &&
        errorResponse.data?.error === "2FA is already enabled for this user."
      ) {
        showErrorToast(
          "2FA is already enabled for your account.",
          "2fa-already-enabled",
        );
        navigate("/app-home");
      } else {
        showErrorToast(
          "Failed to fetch 2FA setup data. Please try again.",
          "2fa-setup-error",
        );
        navigate("/login");
      }
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetch2FASecret();
  }, [fetch2FASecret]);

  // Handle individual digit input
  const handleChange = (value: string, index: number) => {
    if (isNaN(Number(value))) return;

    const newOtp = [...otp];
    newOtp[index] = value.substring(value.length - 1);
    setOtp(newOtp);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number,
  ) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  // Handle clipboard paste
  const handlePaste = (e: React.ClipboardEvent) => {
    const data = e.clipboardData.getData("text").trim();
    if (!/^\d{6}$/.test(data)) return;

    const pasteData = data.split("");
    setOtp(pasteData);
    inputRefs.current[5]?.focus();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const fullCode = otp.join("");

    if (fullCode.length !== 6) {
      showErrorToast("Please enter the full 6-digit code.", "2fa-code-error");
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiService.post<{
        access_token: string;
        refresh_token: string;
        user: { username: string; permissions: string[] };
        is_2fa_enabled: boolean;
        message: string;
      }>("/api/authentication/2fa/verify/", {
        username: twoFaUsername,
        totp_code: fullCode,
      });

      if (response.status === 200) {
        try {
          const themeResponse = await apiService.get<IThemeState>(
            `/api/companies/theme/${encodeURIComponent(
              response.data.user.username,
            )}`,
          );
          dispatch(setTheme(themeResponse.data));
        } catch (themeErr) {
          console.error("Failed to fetch theme:", themeErr);
        }

        showSuccessToast("2FA enabled successfully!");
        navigate("/app-home");
      }
    } catch (err: unknown) {
      const errorInfo = err as {
        message?: string;
        response?: { data?: { error?: string }; status?: number };
      };
      let errorMessage = "Invalid 2FA code. Please try again.";
      if (errorInfo.response?.data?.error) {
        errorMessage = errorInfo.response.data.error;
      }
      showErrorToast(errorMessage, "2fa-verify-error");
    } finally {
      setIsLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.5, staggerChildren: 0.08 },
    },
  };
  const itemVariants = {
    hidden: { opacity: 0, y: 18 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
  };

  return (
    <Box
      sx={{
        height: "100vh",
        width: "100%",
        overflow: "hidden",
        backgroundColor: "#ffffff",
        border: isStaging ? "10px solid #FFBB00" : "none",
        boxSizing: "border-box",
      }}
    >
      <Grid container sx={{ height: "100%" }}>
        {/* LEFT */}
        <Grid
          size={{ xs: 12, md: 6 }}
          sx={{
            height: "100%",
            backgroundColor: "#fff",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            px: { xs: 3, sm: 5, md: 7 },
            py: { xs: 3, md: 4 },
            overflow: "hidden",
          }}
        >
          {isStaging && (
            <Box
              sx={{
                position: "absolute",
                left: -34,
                top: "50%",
                transform: "translateY(-50%) rotate(-90deg)",
                transformOrigin: "left center",
                backgroundColor: "#FFBB00",
                color: "#fff",
                fontWeight: 700,
                fontSize: "11px",
                letterSpacing: "2px",
                textTransform: "uppercase",
                px: 1.5,
                py: 0.6,
                borderRadius: "0 0 6px 6px",
                zIndex: 1000,
              }}
            >
              STAGING
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: { xs: 3, md: 5 },
              flexShrink: 0,
            }}
          >
            <Box
              component="img"
              src={muzukuruLogoToUse}
              alt="Logo"
              sx={{ height: 48, objectFit: "contain" }}
            />
            <Box
              sx={{
                width: 40,
                height: 40,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#6b4c3b"
                strokeWidth="1.5"
              >
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </Box>
          </Box>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              display: "flex",
              justifyContent: "center",
              pr: { xs: 0.5, md: 1 },
              "&::-webkit-scrollbar": { width: "8px" },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "rgba(0,0,0,0.15)",
                borderRadius: "999px",
              },
            }}
          >
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              style={{ maxWidth: 440, width: "100%" }}
            >
              <Box component="form" onSubmit={handleSubmit}>
                <motion.div variants={itemVariants}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: "#1a1a1a",
                      mb: 0.5,
                      fontSize: { xs: "24px", md: "28px" },
                    }}
                  >
                    Set Up 2FA
                  </Typography>
                </motion.div>

                <motion.div variants={itemVariants}>
                  <Typography
                    sx={{
                      color: "#6b6b6b",
                      fontSize: "15px",
                      mb: 3,
                      lineHeight: 1.6,
                    }}
                  >
                    Enhance your account security by enabling two-factor
                    authentication.
                  </Typography>
                </motion.div>

                {/* Step 1: QR Code */}
                <motion.div variants={itemVariants}>
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        color: "#1a1a1a",
                        fontSize: "13px",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        mb: 1,
                      }}
                    >
                      Step 1: Scan the QR Code
                    </Typography>
                    <Typography
                      sx={{ color: "#6b6b6b", fontSize: "14px", mb: 1.5 }}
                    >
                      Use an authenticator app to scan the QR code below.
                    </Typography>
                    {isLoading && !qrCodeUrl ? (
                      <CircularProgress
                        size={24}
                        sx={{ color: "#6b4c3b", mx: "auto", display: "block" }}
                      />
                    ) : qrCodeUrl && !qrError ? (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          mb: 1,
                        }}
                      >
                        <img
                          src={qrCodeUrl}
                          alt="2FA QR Code"
                          style={{
                            width: 180,
                            height: 180,
                            border: "1px solid #e5e5e5",
                            borderRadius: "8px",
                          }}
                          onError={() => setQrError(true)}
                        />
                      </Box>
                    ) : (
                      <Typography
                        sx={{
                          color: "#d32f2f",
                          textAlign: "center",
                          fontSize: "14px",
                        }}
                      >
                        Failed to load QR code. Please use manual setup.
                      </Typography>
                    )}
                  </Box>
                </motion.div>

                {/* Step 2: Manual Key */}
                <motion.div variants={itemVariants}>
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        color: "#1a1a1a",
                        fontSize: "13px",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        mb: 1,
                      }}
                    >
                      Step 2: Manual Setup
                    </Typography>
                    <Box
                      sx={{
                        fontFamily: "monospace",
                        color: "#1a1a1a",
                        wordBreak: "break-all",
                        p: 2,
                        backgroundColor: "#f5f5f5",
                        borderRadius: "8px",
                        fontSize: "13px",
                        border: "1px solid #e5e5e5",
                      }}
                    >
                      {secretKey || "Loading..."}
                    </Box>
                  </Box>
                </motion.div>

                {/* Step 3: 6-Digit Code */}
                <motion.div variants={itemVariants}>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: "#1a1a1a",
                      fontSize: "13px",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      mb: 1,
                    }}
                  >
                    Step 3: Verify 2FA Code
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: { xs: 1, sm: 1.2 },
                      mb: 2,
                      justifyContent: "space-between",
                    }}
                  >
                    {otp.map((digit, index) => (
                      <InputBase
                        key={index}
                        inputRef={(el) => (inputRefs.current[index] = el)}
                        value={digit}
                        onChange={(e) => handleChange(e.target.value, index)}
                        onKeyDown={(e) => handleKeyDown(e, index)}
                        onPaste={index === 0 ? handlePaste : undefined}
                        disabled={isLoading}
                        inputProps={{
                          inputMode: "numeric",
                          maxLength: 1,
                          style: {
                            textAlign: "center",
                            fontSize: "18px",
                            fontWeight: "700",
                            color: "#1a1a1a",
                          },
                        }}
                        sx={{
                          width: "100%",
                          height: "52px",
                          border: "1.5px solid",
                          borderColor: otp[index] ? "#6b4c3b" : "#d4d4d4",
                          borderRadius: "8px",
                          backgroundColor: "#fff",
                          "&:focus-within": {
                            borderColor: "#6b4c3b",
                            boxShadow: "0 0 0 1px #6b4c3b",
                          },
                        }}
                      />
                    ))}
                  </Box>
                </motion.div>

                <motion.div variants={itemVariants}>
                  <Box
                    sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}
                  >
                    <Link
                      to="/login"
                      style={{
                        color: "#6b4c3b",
                        textDecoration: "none",
                        fontSize: "14px",
                        fontWeight: 600,
                      }}
                    >
                      Back to Login
                    </Link>
                  </Box>
                </motion.div>

                <motion.div variants={itemVariants}>
                  <Button
                    fullWidth
                    type="submit"
                    variant="contained"
                    disabled={isLoading || otp.some((d) => d === "")}
                    sx={{
                      backgroundColor: "#6b4c3b",
                      textTransform: "none",
                      padding: "14px",
                      borderRadius: "8px",
                      fontSize: "16px",
                      fontWeight: 600,
                      boxShadow: "none",
                      color: "#fff",
                      "&:hover": {
                        backgroundColor: "#55392d",
                        boxShadow: "none",
                      },
                      "&:disabled": {
                        backgroundColor: "rgba(107,76,59,0.4)",
                        color: "#fff",
                      },
                    }}
                  >
                    {isLoading ? (
                      <CircularProgress size={24} sx={{ color: "#fff" }} />
                    ) : (
                      "Verify and Enable 2FA"
                    )}
                  </Button>
                </motion.div>
                <Box sx={{ height: 32 }} />
              </Box>
            </motion.div>
          </Box>
        </Grid>

        {/* RIGHT */}
        <Grid
          size={{ xs: 0, md: 6 }}
          sx={{
            height: "100%",
            display: { xs: "none", md: "flex" },
            overflow: "hidden",
            backgroundColor: "#fff",
            p: 2,
            boxSizing: "border-box",
          }}
        >
          <CtaSlideshow images={ctaImages} intervalMs={5000} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default Setup2FA;
